import type { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";

/** Scope mặc định cho section token (multi-scope mở rộng sau) */
export const DEFAULT_VERIFICATION_SCOPE = "default" as const;

export type VerificationScope = string;

/** Payload tùy chọn khi gọi logout (khớp pattern logoutStart của app) */
export type LogoutPayload = {
  toastMsg?: string;
};

/**
 * Phụ thuộc inject từ bên ngoài — HttpClient không import Redux/UI.
 * Gắn tại lớp singleton (baseHttp / authHttp).
 */
export interface HttpClientDependencies {
  onLogout?: (payload?: LogoutPayload) => void;
  /**
   * Khi request cần xác thực mà chưa có section token trong memory:
   * mở modal OTP, lấy section token từ backend, resolve chuỗi token.
   *
   * `abortSignal` được truyền khi timeout OTP hoặc khi clear scope — app nên lắng nghe để đóng modal.
   * Callback không tham số vẫn tương thích TypeScript.
   */
  onRequireOtp?: (abortSignal?: AbortSignal) => Promise<string>;
  /**
   * Thời gian tối đa chờ OTP (ms). Hết hạn → reject promise OTP, xóa hàng đợi scope đó.
   * Mặc định 60_000.
   */
  otpFlowTimeoutMs?: number;
}

/**
 * Cờ & metadata gắn trên config Axios (internal).
 * Dùng type guard / helper thay vì so sánh lỏng ở mọi nơi.
 */
export type HttpClientRequestFlags = {
  /**
   * `true` (boolean) = bắt buộc header `X-Section` cho scope đã chọn.
   * Chỉ khi đúng `true` mới kích hoạt luồng OTP — tránh `"yes"` / truthy nhầm.
   */
  requiresVerification?: boolean;
  /**
   * Scope lưu token trong `Map` — mở rộng đa luồng verify sau này.
   * Không set → dùng `DEFAULT_VERIFICATION_SCOPE`.
   */
  verificationScope?: VerificationScope;
  /** Đã retry refresh access token một lần (401). */
  _retry?: boolean;
  /**
   * Đã retry sau 403 ERR_OTP một lần — tránh vòng lặp vô hạn.
   */
  _verifyRetry?: boolean;
};

export type AxiosRequestConfigWithRetry = InternalAxiosRequestConfig &
  HttpClientRequestFlags;

export interface FailedRequestQueueItem {
  resolve: (accessToken: string) => void;
  reject: (reason?: unknown) => void;
}

/**
 * @deprecated Giữ interface để tương thích import cũ; luồng mới dùng Promise/scope map.
 */
export interface VerifyQueueItem {
  resolve: (sectionToken: string) => void;
  reject: (reason?: unknown) => void;
}

/**
 * Config gọi API từ app — tương thích Axios, thêm cờ verified request.
 */
export type HttpClientRequestConfig = AxiosRequestConfig &
  HttpClientRequestFlags;
